var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsDateString, IsEnum, IsInt, IsOptional, IsString, MaxLength, Min, ValidateIf, ValidateNested, } from 'class-validator';
import { Heilmittelbereich } from '../../pvs-billing/enums/heilmittelbereich.enum.js';
import { PhysicianCopaymentMark } from '../enums/physician-copayment-mark.enum.js';
import { PrescriptionFrequency } from '../enums/prescription-frequency.enum.js';
import { PrescribedCatalogOptionInputDto } from './prescribed-catalog-option.dto.js';
export class PrescriptionDataDto {
    prescriptionDate;
    urgentTreatmentNeeded;
    minWeeklyTherapyFrequency;
    maxWeeklyTherapyFrequency;
    prescriptionFrequency;
    prescribingPhysician;
    lanr;
    bsnr;
    icd10Code;
    diagnosisText;
    // --- Structured GKV capture (RS-002) — all optional; pkv/beihilfe omit them.
    /** SDHM Diagnosegruppe code (e.g. `CS`). Code string, never an FK (ADR-0026). */
    diagnosisGroupCode;
    /** Selected Leitsymptomatik codes (a/b/c; multiple allowed). */
    leitsymptomatikCodes;
    /** Patientenindividuelle Leitsymptomatik free text. */
    leitsymptomatikText;
    /** Second Verordnungs-Diagnose ICD code — free-text snapshot, no FK. */
    icd10Code2;
    diagnosisText2;
    /**
     * What the physician wrote about the Diagnose beyond the two ICD-10 lines (TM-9664) —
     * optional free text under them, kept as written.
     */
    diagnosisNote;
    /**
     * The Zuzahlungs-Kreuz on Muster 13.1/E (#1497). Optional on the wire, but not
     * nullable in meaning: omitting it means `NOT_SPECIFIED`, the lawful normal
     * case. The scan path never supplies it — the barcode carries no such field.
     *
     * ⚠ `@ValidateIf` rather than `@IsOptional`, unlike every nullable sibling
     * above. `@IsOptional` waves `null` through as well as `undefined`, and `null`
     * would reach a NOT NULL column as a 500 instead of being refused as the
     * fourth reading it is. Omission is allowed; an explicit `null` is not.
     */
    physicianCopaymentMark;
    /**
     * Feld 16 `Heilmittelart` — the Verordnung's Heilmittelbereich (#1504).
     *
     * Nullable on the wire and optional, because capture never blocks: a
     * Verordnung scanned off a barcode with an unreadable Feld 16 still saves. It
     * is simply not billable until somebody supplies it — which is a question that
     * belongs on the Verordnungsformular, not in the middle of a scan.
     */
    heilmittelbereich;
    /**
     * Feld 31 `Therapiebericht` and Feld 32 `Hausbesuch` — the two boxes the
     * prescriber ticks on Muster 13 that the barcode carries and nothing kept
     * (TM-9607). Both are `ZHE` fields of the §302 Abrechnung.
     *
     * Optional on the wire and never nullable, and the omission reads differently
     * on the two paths. On create it means `false` — what an unticked box says.
     * On update it means *„leave it alone"*, the same PATCH semantics every other
     * field of this DTO has: clearing a recorded tick takes an explicit `false`,
     * not a left-out field.
     *
     * ⚠ `@ValidateIf` rather than `@IsOptional`, exactly as
     * `physicianCopaymentMark` above and for the same reason — `@IsOptional`
     * waves `null` through as well as `undefined`, and a `null` would reach a NOT
     * NULL column as a 500 instead of being refused. There is no third reading of
     * a checkbox to express.
     */
    therapyReportRequested;
    homeVisitPrescribed;
    /**
     * The Heilmittelkatalog options the physician ticked (#1425 slice 1) — one
     * entry per prescribed Heilmittel, keyed naturally by
     * `(diagnosisGroupCode, optionName)`. Omitted (or empty) for non-GKV plans and
     * for a GKV capture made without a Diagnosegruppe; on update, a present array
     * REPLACES the recorded set, `undefined` leaves it untouched.
     */
    prescribedCatalogOptions;
}
__decorate([
    Expose(),
    IsDateString(),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "prescriptionDate", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], PrescriptionDataDto.prototype, "urgentTreatmentNeeded", void 0);
__decorate([
    Expose(),
    IsInt(),
    Min(1),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "minWeeklyTherapyFrequency", void 0);
__decorate([
    Expose(),
    IsInt(),
    Min(1),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "maxWeeklyTherapyFrequency", void 0);
__decorate([
    Expose(),
    IsEnum(PrescriptionFrequency),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "prescriptionFrequency", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(255),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "prescribingPhysician", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(20),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "lanr", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(20),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "bsnr", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(10),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "icd10Code", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(250),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "diagnosisText", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(10),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "diagnosisGroupCode", void 0);
__decorate([
    Expose(),
    IsArray(),
    IsString({ each: true }),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "leitsymptomatikCodes", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(500),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "leitsymptomatikText", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(10),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "icd10Code2", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(250),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "diagnosisText2", void 0);
__decorate([
    Expose(),
    IsString(),
    MaxLength(500),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "diagnosisNote", void 0);
__decorate([
    Expose(),
    ValidateIf((_, value) => value !== undefined),
    IsEnum(PhysicianCopaymentMark),
    __metadata("design:type", String)
], PrescriptionDataDto.prototype, "physicianCopaymentMark", void 0);
__decorate([
    Expose(),
    IsEnum(Heilmittelbereich),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionDataDto.prototype, "heilmittelbereich", void 0);
__decorate([
    Expose(),
    ValidateIf((_, value) => value !== undefined),
    IsBoolean(),
    __metadata("design:type", Boolean)
], PrescriptionDataDto.prototype, "therapyReportRequested", void 0);
__decorate([
    Expose(),
    ValidateIf((_, value) => value !== undefined),
    IsBoolean(),
    __metadata("design:type", Boolean)
], PrescriptionDataDto.prototype, "homeVisitPrescribed", void 0);
__decorate([
    Expose(),
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => PrescribedCatalogOptionInputDto),
    IsOptional(),
    __metadata("design:type", Array)
], PrescriptionDataDto.prototype, "prescribedCatalogOptions", void 0);
//# sourceMappingURL=prescription-data.dto.js.map